import NavBar from "./components/NavBar";
import React, { useEffect, useState } from "react";
import useStore from "../../store/useStore";
import { getAllQuiz, getMasterData } from "./service";
import InProgressQuizCard from "./components/InProgressQuizCard";
import EmptyState from "../../assets/images/EmptyState.svg";
import { useSearchParams } from "react-router-dom";
import { consumeQuizEntrySource, trackZymedEvent, ZYMED_EVENTS } from "../../utils/zymedAnalytics";

const PediaQuiz = () => {

    const [searchParams] = useSearchParams();
    const [activeTab, setActiveTab] = useState<string>(searchParams.get('activeTab')?.toString() || "in-progress");
    const {
        tatvaPediaToken,
    } = useStore();
    // Helper function to get token
    const [loading, setLoading] = useState(true);
    const [contentTypes, setContentTypes] = useState<any>([]);
    const getToken = () =>
        localStorage.getItem("tatvaPediaToken") || tatvaPediaToken;
    const token = getToken();
    const [inProgressQuizzes, setInProgressQuizzes] = useState<any>([]);
    const [finishedQuizzes, setFinishedQuizzes] = useState<any>([]);

    const fetchAllQuizzes = async () => {
        try {
            const response = await getAllQuiz(50, token);
            if (response !== undefined) {
                console.log('All Quizzes {}', response);
                const inProgressQuizzes = response.body.content.filter((q: any) => q?.filled === 'No' && new Date(q?.endDate) > new Date());
                const finishedQuizzes = response.body.content.filter((q: any) => q?.filled === 'Yes');
                setInProgressQuizzes(inProgressQuizzes);
                setFinishedQuizzes(finishedQuizzes);
                console.log("finished quizzes", finishedQuizzes);
                setLoading(false);
            }

        } catch (e) {
            console.log('error in fetching quizzes');
        }
    };

    const getContentTypes = async () => {
        try {
            const response: any = await getMasterData(["ContentType"]);
            if (response !== undefined) {
                setContentTypes(response?.contenttype);
            }
        } catch (e) {
            console.log('error in fetching content types');
        }

    };

    useEffect(() => {
        fetchAllQuizzes();
        getContentTypes();
        trackQuizTab(activeTab, consumeQuizEntrySource());
        // Mount-only: later switches are reported by handleTabChange.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);


    /** Quiz list sub-tabs. The first view is reported from the mount effect below. */
    const trackQuizTab = (tab: string, source: string) =>
        trackZymedEvent(ZYMED_EVENTS.PEDIA_QUIZ_TAB_VIEWED, {
            source,
            sub_tab: tab === "in-progress" ? "Active Quizzes" : "Finished Quizzes",
        });

    const handleTabChange = (tab: typeof activeTab) => {
        trackQuizTab(tab, "pedia_quiz_tab");
        setActiveTab(tab);
    };

    const noQuizAvailable = (tab: string) => {
        return tab === 'inprogress' ? (<div className="flex flex-col items-center justify-center h-full mt-24 w-full">
            <img src={EmptyState} alt="Empty State" width={120} height={120} />
            <span className={"text-2xl font-semibold"}>No Quizzes Found!</span>
            <p className="text-md font-medium text-gray-500 text-center">
                Quizzes will appear here once available.
            </p>
        </div>) : (<div className="flex flex-col items-center justify-center h-full mt-24 w-full">
            <img src={EmptyState} alt="Empty State" width={120} height={120} />
            <span className={"text-xl font-semibold"}>No Finished Quizzes Found!</span>
        </div>);
    };

    return (
        <div className="flex flex-col h-screen">
            {/* Fixed Header Section */}
            <div className="flex-none">
                <div className="bg-white p-4 flex justify-between items-center z-10 text-2xl font-bold text-left">
                    Quiz
                </div>
            </div>
            <div className="shadow-md bg-white">
                <div className="flex justify-around w-full">
                    <button
                        onClick={() => handleTabChange("in-progress")}
                        className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${activeTab === "in-progress" ? "text-indigo-600" : ""
                            }`}
                    >
                        <span
                            className={`flex w-max items-center font-roboto text-base px-4 py-2 leading-6 tracking-[0.25px] ${activeTab === "in-progress"
                                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                                    : ""
                                }`}
                        >
                            Active Quizzes
                        </span>
                        {activeTab === "in-progress" && (
                            <span
                                className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
                        )}
                    </button>

                    <button
                        onClick={() => handleTabChange("finished")}
                        className={`relative flex items-center gap-2 pt-3 pb-1 text-sm font-medium rounded-t-lg ${activeTab === "finished" ? "text-indigo-600" : ""
                            }`}
                    >
                        <span
                            className={`flex w-max items-center font-roboto text-base px-4 py-2 leading-6 tracking-[0.25px] ${activeTab === "finished"
                                    ? "bg-indigo-50 text-indigo-600 rounded-3xl mb-1 font-bold"
                                    : ""
                                }`}
                        >
                            Finished Quizzes
                        </span>
                        {activeTab === "finished" && (
                            <span
                                className="absolute bottom-0 left-1/2 w-[66px] h-[5px] bg-indigo-600 rounded-t-lg -translate-x-1/2"></span>
                        )}
                    </button>
                </div>
            </div>

            {/* Scrollable Content Section */}
            <div className="flex-1 overflow-y-auto">
                {loading && (
                    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-center items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
                    </div>
                )}
                {/*
                    The cards are `w-full max-w-md`, so on a tablet they stop well short
                    of the container. As a plain block that left-aligned them against the
                    empty space, and consecutive white cards sat flush against each other
                    with no separation — reading as one block rather than a list.
                    items-center centres the capped column; gap-4 separates the cards.
                */}
                <div className="px-4 mt-4 mb-20 flex flex-col items-center gap-4">
                    {activeTab === 'in-progress' ? <>
                        {inProgressQuizzes?.length > 0 ? (inProgressQuizzes.map((q: any, i: any): any => (
                            <InProgressQuizCard token={token} tab={activeTab} key={'inprogress' + i} quiz={q}
                                contentTypes={contentTypes} />
                        ))) : noQuizAvailable('inprogress')}
                    </> : <>{finishedQuizzes?.length > 0 ? (finishedQuizzes.map((q: any, i: any): any => (
                        <InProgressQuizCard token={token} tab={activeTab} key={'finished' + i} quiz={q}
                            contentTypes={contentTypes} />
                    ))) : noQuizAvailable('finished')}</>
                    }
                </div>
            </div>
            <NavBar />
        </div>
    );
};

export default PediaQuiz;